import { useState, createRef } from "react"
import { useMountEffect } from "shared/src/libs/hooksLib/hooksLib"
import { Link, useLocation } from "react-router-dom"
import { Group, Burger, Menu, Button, Flex, Text, Anchor, UnstyledButton } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { ChevronDown } from "lucide-react"
import { Row } from "../BSReplace"
import { setTitle } from "../SetDocumentTitle/SetDocumentTitle"
import { BetaBadge } from "@shadcn/components/atoms/badges/BetaBadge"
import araLogo from "../../images/HireAraLogoPurple.png"
import { lazyWithForcedRefresh } from "../../libs/lazyLoading/lazyLoading"
import "./AppSkeleton.css"
import "../CardView/card-view.css" // import here to ensure in main bundle
import "../CVViewing/CVCards/CVCards.css" // import here to ensure in main bundle

const AccessButton = lazyWithForcedRefresh(() => import(/* webpackChunkName: "AccessButton" */ "./AccessButton"))

export const mouseWithinWindow: React.MutableRefObject<boolean> = createRef<boolean | undefined>()

/**
 * Hook to initialize mouseWithinWindow tracking at the app root level.
 * This is needed for blur detection in edit boxes to work correctly.
 * Should be called once at the app root (e.g., in BaseProviders or App component).
 */
export function useMouseWithinWindowTracking() {
	useMountEffect(() => {
		mouseWithinWindow.current = true
		const onMouseLeave = () => {
			mouseWithinWindow.current = false
		}
		const onMouseEnter = () => {
			mouseWithinWindow.current = true
		}
		document.addEventListener("mouseleave", onMouseLeave)
		document.addEventListener("mouseenter", onMouseEnter)
		return () => {
			document.removeEventListener("mouseleave", onMouseLeave)
			document.removeEventListener("mouseenter", onMouseEnter)
		}
	})
}

export function TopLevel({ isAuthenticating, ...props }) {
	useMouseWithinWindowTracking()

	return (
		!isAuthenticating && (
			<div className="AppContainer">
				<div className="main overflow-y-auto relative bg-neutral-50 [&:has(.scroll-lock)]:overflow-hidden">
					{props.children}
				</div>
			</div>
		)
	)
}

interface AppSkeletonProps {
	accessIdToken?: string
	clientId?: string
	evoEnabled?: boolean
	logoLink?: string
	navbar: boolean
	navbarDisabled: boolean
	navbarLinks?: Record<string, string>
	navbarContent?: React.ReactNode
	isAuthenticating: boolean
	logo?: string | null
	userName: string
	handleLogout: () => void
	children?: React.ReactNode
}

export function AppSkeleton({
	accessIdToken,
	clientId,
	evoEnabled,
	logoLink,
	navbar = true,
	navbarDisabled = false,
	navbarLinks,
	navbarContent = null,
	isAuthenticating,
	logo,
	userName,
	handleLogout,
	...props
}: Readonly<AppSkeletonProps>) {
	const linkProps = logoLink ? { component: Link, to: logoLink } : {}
	const { pathname } = useLocation()
	const [mobileOpened, { toggle: toggleMobile }] = useDisclosure()
	const [userMenuOpened, setUserMenuOpened] = useState(false)

	useMountEffect(() => {
		setTitle()
	})

	return (
		<TopLevel isAuthenticating={isAuthenticating}>
			{navbar ? (
				<header id="navbar" style={{ padding: "12px 16px", borderBottom: "1px solid #e0e0e0" }}>
					<Group justify="space-between" align="center">
						<Group>
							<Burger
								opened={mobileOpened}
								onClick={toggleMobile}
								hiddenFrom="lg"
								size="sm"
								aria-label="Toggle navigation"
							/>
							{accessIdToken && evoEnabled && (
								<AccessButton accessIdToken={accessIdToken} clientId={clientId} evoEnabled={evoEnabled} />
							)}
							<UnstyledButton
								{...linkProps}
								style={{ marginRight: 10 }}
								className={navbarDisabled ? "nav-disabled" : "clickable"}>
								<img
									style={{ width: 120, height: 30, objectFit: "contain" }}
									height="30px"
									width="120px"
									src={araLogo}
									alt="ARA"
								/>
							</UnstyledButton>
						</Group>

						<Group className="navbar-links-section" flex={1} gap="md" visibleFrom="lg">
							{Object.entries(navbarLinks ?? {}).map(([text, linkPath]) => (
								<Anchor
									key={text}
									component={Link}
									to={linkPath}
									className={`blueLink ${pathname.startsWith(linkPath) ? "currentPageUnderline" : ""} ${navbarDisabled ? "nav-disabled" : "clickable"}`}
									underline="never">
									<Group gap="xs" wrap="nowrap">
										{text}
										{text === "Sales Assets" && <BetaBadge />}
									</Group>
								</Anchor>
							))}
						</Group>

						<Group gap={0}>
							<Group visibleFrom="lg">
								<Menu
									opened={userMenuOpened}
									onClose={() => {
										setUserMenuOpened(false)
									}}
									position="bottom-end"
									withinPortal={false}>
									<Menu.Target>
										<Button
											variant="subtle"
											className="darkLink"
											style={{ fontSize: 15, minWidth: 200, textAlign: "right" }}
											onClick={() => {
												setUserMenuOpened(!userMenuOpened)
											}}>
											<Group gap="xs" wrap="nowrap">
												<Text>{`Hello, ${userName}`}</Text>
												<ChevronDown size={16} />
											</Group>
										</Button>
									</Menu.Target>
									<Menu.Dropdown>
										<Menu.Item className="darkLink" onClick={handleLogout}>
											Sign Out
										</Menu.Item>
									</Menu.Dropdown>
								</Menu>
							</Group>

							{logo ? (
								<img
									height="60px"
									style={{
										height: 60,
										maxWidth: 145,
										objectFit: "contain",
									}}
									alt="logo"
									src={logo}
								/>
							) : (
								<div style={{ height: 60 }}></div>
							)}
						</Group>
					</Group>

					{mobileOpened && (
						<Flex direction="column" gap="md" mt="md" hiddenFrom="lg" className="navbar-links-section">
							{Object.entries(navbarLinks ?? {}).map(([text, linkPath]) => (
								<Anchor
									key={text}
									component={Link}
									to={linkPath}
									className={`blueLink ${navbarDisabled ? "nav-disabled" : ""}`}
									underline="never"
									onClick={() => {
										toggleMobile()
									}}>
									<Group gap="xs" wrap="nowrap">
										{text}
										{text === "Sales Assets" && <BetaBadge />}
									</Group>
								</Anchor>
							))}
							<Button variant="light" className="darkLink" onClick={handleLogout}>
								Sign Out
							</Button>
						</Flex>
					)}
				</header>
			) : (
				<UnstyledButton {...linkProps}>
					<img style={{ width: 100, margin: "8px 16px" }} src={araLogo} alt="ARA" />
				</UnstyledButton>
			)}
			{navbarContent && (
				<Row className="justify-content-end" style={{ marginBottom: 20 }}>
					{navbarContent}
				</Row>
			)}
			{props.children}
		</TopLevel>
	)
}
